import { inject } from '@angular/core';
import type { NavigationError } from '@angular/router';
import { MessageService } from 'primeng/api';
import { sanitizeReturnUrl } from '../auth/return-url.util';
import { Translation } from '../i18n/translation.service';

const LAZY_ROUTE_RETRY_KEY = 'worknest.lazyRouteRetry';
const RETRY_WINDOW_MS = 30_000;

type LazyRouteRetry = {
  url: string;
  at: number;
};

export function recoverLazyRouteNavigationError(error: NavigationError): void {
  if (!isLazyRouteLoadError(error.error) || typeof window === 'undefined') {
    return;
  }

  const targetUrl = sanitizeReturnUrl(normalizeTargetUrl(error.url));
  if (shouldReloadOnce(targetUrl)) {
    window.location.replace(withReloadCacheBuster(targetUrl));
    return;
  }

  clearRetryState();
  const toast = inject(MessageService);
  const translation = inject(Translation);
  toast.add({
    severity: 'error',
    summary: translation.instant('COMMON.ERROR'),
    detail: translation.instant('COMMON.ERROR_NETWORK'),
  });
}

function isLazyRouteLoadError(error: unknown): boolean {
  const message = collectErrorText(error).toLowerCase();
  return (
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('importing a module script failed') ||
    message.includes('failed to load module script') ||
    message.includes('chunkloaderror') ||
    message.includes('loading chunk')
  );
}

function collectErrorText(error: unknown): string {
  if (!error) {
    return '';
  }

  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    const cause = 'cause' in error ? collectErrorText(error.cause) : '';
    return `${error.name} ${error.message} ${cause}`;
  }

  try {
    return String(error);
  } catch {
    return '';
  }
}

function normalizeTargetUrl(url: string): string {
  const fallback = `${window.location.pathname}${window.location.search}${window.location.hash}` || '/';
  if (!url) {
    return fallback;
  }

  try {
    const parsed = new URL(url, window.location.origin);
    return `${parsed.pathname}${parsed.search}${parsed.hash}` || '/';
  } catch {
    return url.startsWith('/') ? url : fallback;
  }
}

function withReloadCacheBuster(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.set('_wnReload', Date.now().toString());
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return url;
  }
}

function shouldReloadOnce(url: string): boolean {
  const now = Date.now();
  const previous = readRetryState();
  if (previous?.url === url && now - previous.at < RETRY_WINDOW_MS) {
    return false;
  }

  writeRetryState({ url, at: now });
  return true;
}

function readRetryState(): LazyRouteRetry | null {
  try {
    const raw = sessionStorage.getItem(LAZY_ROUTE_RETRY_KEY);
    return raw ? (JSON.parse(raw) as LazyRouteRetry) : null;
  } catch {
    return null;
  }
}

function writeRetryState(value: LazyRouteRetry): void {
  try {
    sessionStorage.setItem(LAZY_ROUTE_RETRY_KEY, JSON.stringify(value));
  } catch {
    // Ignore storage failures; the hard reload is still the best recovery path.
  }
}

function clearRetryState(): void {
  try {
    sessionStorage.removeItem(LAZY_ROUTE_RETRY_KEY);
  } catch {
    // Ignore storage failures.
  }
}
